import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";

import { useEditContext } from "../../hooks/useEditContext";
import useProfileById from "../../hooks/useProfileById";
import useUserObjects from "../../hooks/useUserObjects";
import useRemoveFromUser from "../../hooks/useRemoveFromUser";
import { displayDate } from "../../utils/date";
import { useLocalizedPath } from "../../hooks/useLocale";

import DisplayItems from "../../components/ui/pagination/DisplayItems";
import Tbody from "../../components/ui/table/Tbody";
import Paginate from "../../components/ui/Paginate";
import Alert from "../../components/alert/Alert";
import Button from "../../components/ui/button";
import Td from "../../components/ui/table/Td";
import Th from "../../components/ui/table/Th";
import Tr from "../../components/ui/table/Tr";
import { COwithoutFeatDataType } from "../../types/connected-object";
import { getFeatureLabel } from "../../utils/features";
import { getCompleteUrl } from "../../utils/url";

import AddIcon from "../../components/icons/Add";
import DeleteIcon from "../../components/icons/Delete";
import PageSectionHeader from "../../components/PageSectionHeader";
import SettingIcon from "../../components/icons/Setting";
import CroixIcon from "../../components/icons/Close";
import Assign from "../../components/asign-object/Assign";
import { Helmet } from "react-helmet-async";

type ObjToRemove = {
  id: number;
  url: string;
}

export default function User () {
  const intl = useIntl();
  const localizedPath = useLocalizedPath();
  const params = useParams();
  const userId = params['id'];
  const validID = !!userId && !isNaN(+userId);
  const navigate = useNavigate();
  const {connectedObjects, colSorted, metaData, getObjectOf, handleSort} = useUserObjects();
  const {removeObj, removed, errorWhenRemove} = useRemoveFromUser(validID ? +userId : 0);
  const {profile, profileNotFound, getProfileById} = useProfileById();
  const {editObject} = useEditContext();
  const [objToRemove, setObjToRemove] = useState<ObjToRemove | null>(null);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);

  useEffect(() => {
    if (!validID) {
      navigate(localizedPath('/dashboard/admin/users'));
    } else {
      getProfileById(+userId);
      getObjectOf(+userId);
    }
  }, [userId, getObjectOf, navigate, getProfileById, validID, localizedPath])

  useEffect(() => {
    if (removed && userId) {
      getObjectOf(+userId);
      setObjToRemove(null);
    }
  }, [getObjectOf, removed, userId])

  const onAssigned = useCallback(() => getObjectOf(+userId!), [getObjectOf, userId]);

  function edit(object: COwithoutFeatDataType) {
    editObject(object);
    navigate(localizedPath('/dashboard/admin/objects/edit'));
  }

  if (profileNotFound) {
    return <>
    <div className="w-full h-full grid place-items-center">
      <p className="text-3xl">{intl.formatMessage({ id: "users.profile.profileNotFound" })}</p>
    </div>
    </>
  }

  return(
    <>
      <Helmet>
        <title>
        { profile?.lastName
        ? intl.formatMessage(
            { id: "users.profile.documentTitle" },
            { lastName: profile.lastName, appName: import.meta.env.VITE_APP_NAME }
          )
        : intl.formatMessage(
            { id: "users.profile.documentTitleFallback" },
            { appName: import.meta.env.VITE_APP_NAME }
          )
        }  
        </title>        
      </Helmet>
      <PageSectionHeader className="mb-12">
        <h2 className="text-xl font-normal text-shadow">
          {intl.formatMessage(
            { id: "users.profile.heading" },
            { lastName: profile?.lastName }
          )}
        </h2>
      </PageSectionHeader>
      {/* Info */}
      <section className="mb-8">
        <h3 className="mb-5 text-sm text-shadow font-bold">
          {intl.formatMessage({ id: "users.profile.personalInfo" })}
        </h3>
        <div className="mb-5 grid md:grid-cols-2 gap-x-5 gap-y-3.5">
          <div>
            <p className="mb-1.5 text-sm">{intl.formatMessage({ id: "users.profile.lastName" })}</p>
            <p className="text-lg font-medium">{profile?.lastName}</p>
          </div>
          <div>
            <p className="mb-1.5 text-sm">{intl.formatMessage({ id: "users.profile.firstName" })}</p>
            <p className="text-lg font-medium">{profile?.firstName}</p>
          </div>
          <div>
            <p className="mb-1.5 text-sm">{intl.formatMessage({ id: "users.profile.email" })}</p>
            <p className="text-lg font-medium">{profile?.email}</p>
          </div>
          <div>
            <p className="mb-1.5 text-sm">{intl.formatMessage({ id: "users.profile.role" })}</p>
            <p className="text-lg font-medium">
              {profile && (
                profile.role === 'admin'
                  ? intl.formatMessage({ id: "users.profile.roleAdmin" })
                  : intl.formatMessage({ id: "users.profile.roleClient" })
              )}
            </p>
          </div>
          <div>
            <p className="mb-1.5 text-sm">{intl.formatMessage({ id: "users.profile.company" })}</p>
            <p className="text-lg font-medium">{profile?.company || intl.formatMessage({ id: "common.dash" })}</p>
          </div>
          <div>
            <p className="mb-1.5 text-sm">{intl.formatMessage({ id: "users.profile.phone" })}</p>
            <p className="text-lg font-medium">{profile?.phone || intl.formatMessage({ id: "common.dash" })}</p>
          </div>
          <div>
            <p className="mb-1.5 text-sm">{intl.formatMessage({ id: "users.profile.signupDate" })}</p>
            <p className="text-lg font-medium">{profile?.createdAt && displayDate(profile.createdAt)}</p>
          </div>
          <div>
            <p className="mb-1.5 text-sm">{intl.formatMessage({ id: "users.profile.activationDate" })}</p>
            <p className="text-lg font-medium">
              {profile && profile.accountActivateAt
                ? displayDate(profile.accountActivateAt)
                : intl.formatMessage({ id: "users.profile.notActivated" })}
            </p>
          </div>
        </div>
      </section>
      
      {/* Assign object to user */}
      {
        profile && (
          <>
            <Button size="medium" width="fit" theme="primary" className="my-5 ml-auto px-4"
              onClick={() => setIsAssignModalOpen(true)}>
              <AddIcon className="inline-block me-3" />
              {intl.formatMessage({ id: "users.profile.assignObject" })}
            </Button>
            <div className={`fixed top-0 right-0 w-full h-full flex items-center justify-end transition-colors
              ${isAssignModalOpen ? "bg-black/35 pointer-events-auto" : "pointer-events-none"}`}
              data-overlay
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              onClick={(e: any) => e.target.dataset?.overlay && setIsAssignModalOpen(false)}>
              <div className={`pt-16 pb-10 px-8 w-full md:w-1/2 lg:w-1/4 bg-slate-200 rounded-lg relative
                transition-transform ${isAssignModalOpen ? 'mx-5 md:mx-8 lg:mx-10 translate-x-0' : 'translate-x-full'}`}>
                <button onClick={() => setIsAssignModalOpen(false)} className="p-1.5 absolute top-5 right-5"><CroixIcon /></button>
                {
                  isAssignModalOpen &&
                  <Assign userId={profile.id} userName={profile.lastName} onSuccess={onAssigned} />
                }
              </div>
            </div>
          </>
        )
      }
      
      { connectedObjects && connectedObjects.length > 0 ? (
        <section className="mb-8">
          { metaData && (
            <DisplayItems
              itemLabel={intl.formatMessage({ id: "users.profile.assignedObjectsLabel" })}
              {...metaData}
            />
          )}
          {/* Table */}
          <div className="py-2 px-1 overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="capitalize">
                  <Th width="150px">{intl.formatMessage({ id: "users.profile.colName" })}</Th>
                  <Th sorted={true} sort={{active: colSorted === "url", target: "url", onSort: handleSort}} width="30%">
                    {intl.formatMessage({ id: "users.profile.colBadgeNumber" })}
                  </Th>
                  <Th sorted={true} sort={{active: colSorted === "feat", target: "feat", onSort: handleSort}}>
                    {intl.formatMessage({ id: "users.profile.colFeature" })}
                  </Th>
                  <Th sorted={true} sort={{active: colSorted === "activatedAt", target: "activatedAt", onSort: handleSort}}>
                    {intl.formatMessage({ id: "users.profile.colActivatedAt" })}
                  </Th>
                  <Th width="80px">{''}</Th>
                </tr>
                <tr><th className="pb-[14px]" colSpan={3}></th></tr>
              </thead>
              <Tbody>
                { connectedObjects.map((obj, i) => (
                    <Tr key={obj.id} isFirstRow={i === 0}>
                      <Td>
                        {obj.name ||''}                        
                      </Td>
                      <Td>                    
                        <a className={`${obj.activeFeature ? '' : 'pointer-events-none opacity-60'} hover:underline`}
                          href={getCompleteUrl(obj.url)} target="_target">
                          {obj.url}
                        </a>
                      </Td>
                      <Td>
                        {
                          obj.activeFeature ? (
                            getFeatureLabel(intl, obj.activeFeature.type)!
                          ) : (
                            intl.formatMessage({ id: "common.dash" })
                          )
                        }
                      </Td>
                      <Td hasBorder={true}>{displayDate(obj.activatedAt)}</Td>
                      <Td hasBorder={false}>
                        <button className="text-shadow hover:text-red-500 me-2"
                          onClick={() => setObjToRemove({id: obj.id, url: obj.url})}>
                          <DeleteIcon  className="w-5 h-5"/>
                        </button>
                        <button
                          className="text-shadow disabled:text-izygray hover:text-primary"
                          onClick={() => edit(obj)}>
                          <SettingIcon className="w-5 h-5"/>
                        </button>
                      </Td>
                    </Tr>
                  ))
                }
              </Tbody>
            </table>
          </div>
          {/* Pagination */}
          
          { metaData && metaData.totalPages > 1 && userId &&
            <div className="my-5 ml-auto w-fit">
              <Paginate {...metaData} to={page => getObjectOf(+userId, page)}/>
            </div>
          }
        </section> ) : (
        <section className="my-8">
          <p className="text-2xl text-center font-medium">
            {intl.formatMessage({ id: "users.profile.emptyAssigned" })}
          </p>
        </section>
      )}

      {/* Confirmation modal */}
      { objToRemove && (
        <div className="fixed w-dvw h-dvh bg-black/30 top-0 left-0 grid place-items-center">
          <div className="px-8 py-5 w-fit bg-white shadow-sm rounded-lg">
            <h3 className="mb-3 text-2xl font-semibold text-shadow">
              {intl.formatMessage(
                { id: "users.profile.removeTitle" },
                { lastName: profile?.lastName }
              )}
            </h3>
            <p className="mb-5 text-shadow">
              {intl.formatMessage(
                { id: "users.profile.removeBody" },
                {
                  url: objToRemove.url,
                  lastName: profile?.lastName
                }
              )}
            </p>
            <div className="flex gap-3 ml-auto w-fit">
              <Button size="small" width="fit" theme="black" className="px-4 uppercase"
                onClick={() => setObjToRemove(null)}>
                {intl.formatMessage({ id: "common.cancel" })}
              </Button>
              <Button size="small" width="fit" theme="danger" className="px-4 uppercase"
                onClick={() => removeObj(objToRemove.id)}>
                {intl.formatMessage({ id: "common.continue" })}
              </Button>
            </div>
            {errorWhenRemove && <Alert message={errorWhenRemove.message} type="error" className="mt-5" autoHidden={false}/>}
          </div>
        </div>
      )}
    </>
  )  
}
